import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Container, Empty, Stack } from '@easymoving/ui';
import { useItems } from '../../hooks/useUserData';
import { computeRoomProgress } from '../../lib/query/roomProgress';
import { roomLabel } from '../../lib/taxonomy/options';
import { AddItemBar } from '../../components/items/AddItemTile';

/** Per-room packing progress (PRD §10.3). */
export function RoomProgressPage() {
  const { t } = useTranslation();
  const { items, loading } = useItems();
  const rows = useMemo(() => computeRoomProgress(items), [items]);

  if (loading) {
    return (
      <Container className="pt-6">
        <p className="text-graphite py-12">{t('common.loading')}</p>
      </Container>
    );
  }
  if (rows.length === 0) {
    return (
      <Container className="pt-10">
        <Empty title={t('rooms.title')} body={t('rooms.empty')} />
      </Container>
    );
  }

  return (
    <Container size="narrow" className="pt-6 pb-8">
      <h1 className="font-display text-2xl tracking-snug mb-4">{t('rooms.title')}</h1>
      <Stack gap="sm">
        {rows.map((r) => {
          const pct = r.total > 0 ? Math.round((r.packed / r.total) * 100) : 0;
          const label = r.room === '' ? t('rooms.noRoom') : roomLabel(r.room, t);
          const body = (
            <>
              <div className="flex items-center justify-between gap-3">
                <span className="font-medium text-[15px]">{label}</span>
                <span className="text-graphite-soft text-[13px] tnum">
                  {t('rooms.packedOf', { packed: r.packed, total: r.total })}
                </span>
              </div>
              <div className="h-2 rounded-full bg-surface-tint overflow-hidden mt-2">
                <div className="h-full bg-petrol rounded-full" style={{ width: `${pct}%` }} />
              </div>
              {r.needsDecision > 0 && (
                <div className="text-amber text-[12px] mt-1.5">
                  {t('rooms.needsCount', { count: r.needsDecision })}
                </div>
              )}
            </>
          );
          return r.room === '' ? (
            <div key="__none__" className="surface p-4">
              {body}
            </div>
          ) : (
            <Link
              key={r.room}
              to={`/app/items?room=${encodeURIComponent(r.room)}`}
              className="surface p-4 block hover:bg-surface-tint transition-colors"
            >
              {body}
            </Link>
          );
        })}
        <AddItemBar />
      </Stack>
    </Container>
  );
}
